/**
 * ViewConfig — DESIGN.md §7.1.
 *
 * One config type drives every card surface: project lists, kanban, calendar,
 * My Tasks, report drill-downs, CSV export, bulk "select all matching", and
 * the MCP `search_cards` tool. Adding a view type is a new renderer over this
 * config, never a new backend query (§18.4 clause 3).
 */
import { z } from 'zod';
import { PRIORITIES, STATUS_CATEGORIES, VIEW_TYPES } from '../enums.js';
/**
 * Filterable fields. `customField:<uuid>` is the open-ended one — custom
 * fields are data (§6.7), so the field name carries the id.
 */
export const FILTER_FIELDS = [
    'status',
    'statusCategory',
    'assignee',
    'creator',
    'collaborator',
    'tags',
    'priority',
    'dueDate',
    'startDate',
    'completedAt',
    'createdAt',
    'updatedAt',
    'project',
    'section',
    'completed',
    'blocked',
    'hasAttachments',
    'hasSubtasks',
    'isSubtask',
    'estimateMinutes',
    'loggedMinutes',
    'text',
];
const CUSTOM_FIELD_RE = /^customField:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const filterFieldSchema = z.union([
    z.enum(FILTER_FIELDS),
    z.string().regex(CUSTOM_FIELD_RE, 'Expected customField:<uuid>'),
]);
export const FILTER_OPS = [
    'eq',
    'neq',
    'in',
    'notIn',
    'isSet',
    'notSet',
    'before',
    'after',
    'between',
    'contains',
    'gt',
    'lt',
];
/**
 * Relative date tokens. Stored rather than resolved so a saved view called
 * "Due this week" keeps meaning this week — resolution happens per request in
 * the viewer's timezone (D10).
 */
export const RELATIVE_DATES = [
    'today',
    'tomorrow',
    'yesterday',
    'thisWeek',
    'nextWeek',
    'lastWeek',
    'thisMonth',
    'overdue',
    'next7Days',
    'next30Days',
    'last7Days',
    'last30Days',
];
export const filterValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.union([z.string(), z.number()])),
]);
export const filterSchema = z.object({
    field: filterFieldSchema,
    op: z.enum(FILTER_OPS),
    value: filterValueSchema.optional(),
});
export const SORT_FIELDS = [
    'manual',
    'number',
    'title',
    'status',
    'priority',
    'assignee',
    'dueDate',
    'startDate',
    'createdAt',
    'updatedAt',
    'completedAt',
    'estimateMinutes',
    'loggedMinutes',
];
export const sortSchema = z.object({
    field: z.union([z.enum(SORT_FIELDS), z.string().regex(CUSTOM_FIELD_RE)]),
    dir: z.enum(['asc', 'desc']),
});
export const GROUP_BY_FIELDS = [
    'status',
    'statusCategory',
    'assignee',
    'section',
    'priority',
    'dueDate',
    'project',
    'tags',
];
export const groupBySchema = z.union([z.enum(GROUP_BY_FIELDS), z.string().regex(CUSTOM_FIELD_RE)]);
/** Columns a list view can render. Custom fields use the `customField:` form. */
export const LIST_COLUMNS = [
    'number',
    'title',
    /**
     * #54 — the identifier mark, `26090101`. A column rather than eight digits
     * on the front of every title, which is what it was until Brad's ruling on
     * that card: "can this be on the card as a property rather than on the
     * title". Off by default, so a list is not carrying it for the many people
     * who never look it up.
     */
    'mark',
    'assignee',
    'status',
    'priority',
    'dueDate',
    'startDate',
    'project',
    'section',
    'tags',
    'estimateMinutes',
    'loggedMinutes',
    'createdAt',
    'updatedAt',
    'completedAt',
    'creator',
    'subtaskProgress',
    'commentCount',
    'attachmentCount',
];
export const columnSchema = z.union([z.enum(LIST_COLUMNS), z.string().regex(CUSTOM_FIELD_RE)]);
/**
 * One label per column, for every surface that names one: the CSV export's
 * header row and the list view's column picker (#29). Shared because they must
 * agree — a column called "Created by" in the export and "Creator" in the
 * picker is two vocabularies for one concept, which rule 2 exists to prevent.
 *
 * Wider than `LIST_COLUMNS`: the exporter also emits `url`, `statusCategory`
 * and `blocked`, which are not things a list renders.
 */
export const COLUMN_LABELS = {
    number: 'Number',
    url: 'URL',
    title: 'Title',
    mark: 'Identifier',
    status: 'Status',
    statusCategory: 'Status category',
    priority: 'Priority',
    assignee: 'Assignee',
    creator: 'Created by',
    dueDate: 'Due date',
    startDate: 'Start date',
    completedAt: 'Completed at',
    createdAt: 'Created at',
    updatedAt: 'Updated at',
    project: 'Projects',
    section: 'Sections',
    tags: 'Tags',
    estimateMinutes: 'Estimate',
    loggedMinutes: 'Logged',
    subtaskProgress: 'Subtasks',
    commentCount: 'Comments',
    attachmentCount: 'Attachments',
    blocked: 'Blocked',
};
/**
 * Columns the list view lets you choose between (#29).
 *
 * `number` and `title` are absent because they are the row's identity and its
 * link, not decoration — a list of cards you cannot identify is not a list.
 * `tags` is absent because it already renders beside the title, and offering
 * it here would draw it twice.
 */
export const SELECTABLE_LIST_COLUMNS = LIST_COLUMNS.filter((column) => column !== 'number' && column !== 'title' && column !== 'tags');
export const DEFAULT_LIST_COLUMNS = [
    'number',
    'title',
    'assignee',
    'dueDate',
    'status',
    'priority',
];
/**
 * A section, as a view refers to one: its uuid, or the literal `'none'` for the
 * project's implicit "(No section)" bucket (§6.1).
 */
export const sectionSelectorSchema = z.union([z.literal('none'), z.string().uuid()]);
/**
 * How a project-scoped view treats sections — §6.1, §7.2.
 *
 * Three independent decisions, because they are three different questions a
 * person asks of a view: *are* the containers there (`mode`), which ones am I
 * not interested in at all (`exclude`), and what should be open when the view
 * opens (`collapsed`).
 *
 * `collapsed: 'remember'` is the default because that is what "unused sections
 * stay collapsed" means: the state belongs to the person, not the view. A view
 * that names `'all'`, `'none'` or a list of ids overrides that for everyone
 * who opens it, without overwriting anybody's own memory (see the web's
 * section-state hook).
 */
export const sectionsConfigSchema = z
    .object({
    /** `flat` drops the containers and shows every card in one run. */
    mode: z.enum(['grouped', 'flat']).default('grouped'),
    /** Sections whose cards this view does not show at all. Filtered server-side. */
    exclude: z.array(sectionSelectorSchema).max(200).default([]),
    collapsed: z
        .union([z.enum(['remember', 'all', 'none']), z.array(sectionSelectorSchema).max(200)])
        .default('remember'),
})
    .strict();
export const viewConfigSchema = z
    .object({
    filters: z.array(filterSchema).max(50).default([]),
    sort: z
        .array(sortSchema)
        .max(5)
        .default([{ field: 'manual', dir: 'asc' }]),
    groupBy: groupBySchema.optional(),
    columns: z.array(columnSchema).max(40).default(DEFAULT_LIST_COLUMNS),
    kanban: z
        .object({
        columnSource: z.union([z.enum(['status', 'section']), z.string().regex(CUSTOM_FIELD_RE)]),
        hideEmpty: z.boolean().default(false),
    })
        .optional(),
    calendar: z
        .object({ dateField: z.enum(['dueDate', 'startDate']).default('dueDate') })
        .optional(),
    /**
     * §6.1 — sections, on a project-scoped list. Absent means the defaults in
     * `effectiveSections`, so every view that predates this field groups by
     * section without being rewritten.
     */
    sections: sectionsConfigSchema.optional(),
    /** R18 — completed cards are excluded unless explicitly asked for. */
    showCompleted: z.boolean().default(false),
    /** §6.4 — subtasks clutter cross-project lists; hidden by default. */
    showSubtasks: z.enum(['hidden', 'nested', 'flat']).default('hidden'),
    /** Scopes the view to one project; `null` is a cross-project view. */
    projectId: z.string().uuid().nullish(),
})
    .strict();
/** The config a brand-new list view starts from. */
export function defaultViewConfig(overrides = {}) {
    return viewConfigSchema.parse({
        filters: [],
        sort: [{ field: 'manual', dir: 'asc' }],
        columns: DEFAULT_LIST_COLUMNS,
        showCompleted: false,
        showSubtasks: 'hidden',
        ...overrides,
    });
}
/**
 * The section behaviour a config actually gets — the one place that decides it,
 * shared by the query engine and every renderer so they cannot disagree.
 *
 * `null` for a cross-project view: sections belong to a project, so "grouped by
 * section" has no meaning across several (§6.1). Grouped is the default for a
 * project-scoped view because a project's sections are how its owner arranged
 * it; a view that wants the flat run says so.
 */
export function effectiveSections(config) {
    if (!config.projectId)
        return null;
    return config.sections ?? { mode: 'grouped', exclude: [], collapsed: 'remember' };
}
export const viewTypeSchema = z.enum(VIEW_TYPES);
export const statusCategorySchema = z.enum(STATUS_CATEGORIES);
export const prioritySchema = z.enum(PRIORITIES);
//# sourceMappingURL=view-config.js.map